import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, CreditCard, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, fmtMoney, errMsg } from "@/lib/api";

export default function PlanChangePay() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/public/plan-change/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  useEffect(() => {
    if (!sid) { return undefined; }
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      try { const r = await axios.get(`${API}/public/plan-change/${token}/status/${sid}`); if (r.data.status === "completato") { setInfo((s) => ({ ...s, status: "completato" })); clearInterval(t); } } catch { /* retry */ }
      if (n > 15) { clearInterval(t); }
    }, 2000);
    return () => clearInterval(t);
  }, [sid, token]);
  const pay = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/public/plan-change/${token}/checkout`, { origin_url: window.location.origin }); window.location.href = r.data.checkout_url; }
    catch (e) { setErr(errMsg(e)); setBusy(false); }
  };
  const done = info?.status === "completato";
  return (
    <div className="min-h-screen bg-black text-zinc-100 grid place-items-center p-6" data-testid="plan-change-page">
      <div className="w-full max-w-xl min-w-0 rounded-2xl border border-zinc-800 bg-zinc-950 p-5 sm:p-8">
        <div className="inline-block bg-white rounded-xl px-4 py-2.5 mb-6"><img src="/nolium-logo-full.png" alt="NOLIUM" className="h-8 w-auto" /></div>
        <div className="text-xs uppercase tracking-widest text-[#11A9A2] font-semibold">Cambio piano</div>
        <h1 className="text-3xl sm:text-4xl font-bold mt-1" data-testid="plan-change-company">{info?.company || "Cambio piano"}</h1>
        {err && <div className="mt-6 flex gap-2 text-sm text-red-300" data-testid="plan-change-error"><AlertTriangle size={16} />{err}</div>}
        {!info && !err && <Loader2 className="animate-spin mt-8 text-zinc-500" />}
        {info && <p className="text-sm text-zinc-400 mt-3">Passaggio dal piano {info.from_plan?.toUpperCase()} al piano <b className="text-white">{info.to_plan_name}</b>. Nuovo canone {fmtMoney(info.monthly_fee)} + IVA al mese.</p>}
        {done && <div className="mt-6 flex gap-3 items-center text-emerald-300" data-testid="plan-change-done"><CheckCircle2 />Grazie! Il piano {info.to_plan_name} è attivo. Ti abbiamo inviato una email con le nuove condizioni.</div>}
        {info && !done && info.status === "annullato" && <div className="mt-6 text-sm text-zinc-400" data-testid="plan-change-cancelled">Questo cambio piano è stato annullato.</div>}
        {info && !done && info.status === "in_attesa" && sid && <div className="mt-6 flex gap-2 items-center text-zinc-400" data-testid="plan-change-checking"><Loader2 className="animate-spin" size={16} />Verifica del pagamento in corso…</div>}
        {info && !done && info.status === "in_attesa" && !sid && <>
          <div className="mt-6 rounded-xl border border-zinc-800 p-5">
            <div className="text-sm text-zinc-400">Differenza sul costo di attivazione</div>
            <div className="text-3xl font-bold text-white mt-1" data-testid="plan-change-gross">{fmtMoney(info.setup_diff_gross)}</div>
            <div className="text-xs text-zinc-400">IVA {info.vat_rate}% inclusa ({fmtMoney(info.setup_diff)} + IVA), pagamento una tantum</div>
          </div>
          <Button className="w-full mt-6 h-12 bg-[#11A9A2] hover:bg-[#0e8f89] text-white" disabled={busy} onClick={pay} data-testid="plan-change-pay-btn">
            {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <CreditCard size={16} className="mr-2" />}Paga {fmtMoney(info.setup_diff_gross)}</Button>
        </>}
      </div>
    </div>
  );
}
